<template>
  <v-container fluid ma-0 pa-0 mb-4 px-4 id="bucketnew-main">
    <Loading :isLoading="loading">
      <div class="fixed-header" :class="{ mobile: $vuetify.breakpoint.smAndDown }">
        <!-- if has summary fields add class 'with-summary' -->
        <v-layout align-center fill-height class="fixed-header-wrapper">
          <v-flex shrink class="text-truncate" pr-4>
            <v-tooltip color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <h2 v-on="on" class="p-relative text-truncate">Overview</h2>
              </template>
              <span>{{ pluralizedName }} Overview</span>
            </v-tooltip>
            <h4 class="pt-1 sub-header d-inline-block">
              Bucket Groups - <span class="font-weight-bold">{{ orderedBucketGroups.length }}</span>
            </h4>
            <h4 class="pt-1 sub-header d-inline-block pl-3">
              Buckets - <span class="font-weight-bold">{{ orderedBuckets.length }}</span>
            </h4>
          </v-flex>
          <v-spacer></v-spacer>
          <v-divider vertical v-if="hasImportPermission"></v-divider>
          <v-flex shrink v-if="hasImportPermission" fill-height class="action">
            <v-tooltip color="secondary" bottom>
              <v-btn icon flat slot="activator" :to="{ path: importRoute(this.caseTypeId) }">
                <v-icon color="secondary" size="20"> publish </v-icon>
              </v-btn>
              <span>Import cases</span>
            </v-tooltip>
          </v-flex>
          <v-divider vertical></v-divider>
          <v-flex shrink fill-height class="action">
            <v-tooltip left color="secondary">
              <v-btn icon flat slot="activator" @click="createNewDialog = true">
                <v-icon color="success" size="24">add_circle</v-icon>
              </v-btn>
              <span>Create a new bucket/bucket group</span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </div>
      <v-dialog v-model="createNewDialog" width="400">
        <v-card class="pa-4">
          <v-card-title class="ma-0 mb-2 pa-0">
            <h2 class="mb-2">What would you like to create?</h2>
          </v-card-title>
          <v-card-text class="ma-0 pa-0">
            <v-flex d-flex justify-flex-end>
              <v-btn :to="{ path: bucketCreateRoute(this.caseTypeId) }" class="ma-0 mr-1" color="success"><v-icon class="mr-2">add_circle</v-icon>Bucket</v-btn>
              <v-btn :to="{ path: bucketGroupCreateRoute(this.caseTypeId) }" class="ma-0 ml-1" color="success"><v-icon class="mr-2">add_circle</v-icon>bucket Group</v-btn>
            </v-flex>
          </v-card-text>
          <v-card-actions class="ma-0 mt-3 pa-0">
            <v-btn @click="createNewDialog = false" color="error">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-layout wrap>
        <v-flex xs12 pt-1>
          <v-btn :ripple="false" flat class="ml-0 mb-2 search-button" v-on:click="bucketsToggled = !bucketsToggled">
            <v-icon color="secondary" class="mr-2 mt-0">{{ bucketsToggled ? "close" : "search" }}</v-icon>
            {{ bucketsToggled ? "Close search" : "Search" }}
          </v-btn>
          <v-slide-y-transition>
            <v-layout row wrap class="mt-0" v-if="bucketsToggled">
              <v-flex xs12 sm6 md4 pt-1 :class="{ 'pr-0 ': $vuetify.breakpoint.xsAndDown, 'pr-2': $vuetify.breakpoint.smAndUp }">
                <v-text-field box class="cardcontainer-search-text pa-0 ma-0 body-1" v-model="searchTerm" label="Search" clearable color="secondary" clear-icon="cancel">
                  <template slot="append">
                    <v-icon>search</v-icon>
                  </template>
                </v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4 pt-1 :class="{ 'pl-0': $vuetify.breakpoint.xsAndDown, 'pl-2': $vuetify.breakpoint.smAndUp }">
                <v-select box style="border-bottom: 0 !important" class="cardcontainer-order-select pa-0 ma-0 body-1" :items="orderByOptions" v-model="orderSelection" label="Order by" clearable color="secondary" clear-icon="cancel" append-icon="keyboard_arrow_down"> </v-select>
              </v-flex>
            </v-layout>
          </v-slide-y-transition>
        </v-flex>
      </v-layout>
      <v-layout wrap fluid row class="card-margin-fix">
        <BucketGroupTile :bucketGroup="bucketGroup" v-for="bucketGroup in orderedBucketGroups" :key="`bucket-group-${bucketGroup.id}`"></BucketGroupTile>
        <BucketTile :bucket="bucket" v-for="bucket in orderedBuckets" :key="`bucket-${bucket.id}`" :permissions="permissions" :show-statistics="true"></BucketTile>
      </v-layout>
    </Loading>
  </v-container>
</template>

<script>
import Helpers from "@/helpers.js"
import { mapState } from "vuex"
import Loading from "@/components/shared/Loading.vue"
import BucketTile from "@/components/buckets/bucket-elements/BucketTile"
import BucketGroupTile from "@/components/buckets/bucket-elements/BucketGroupTile"

export default {
  name: "CaseTemplateOverview",
  components: {
    Loading,
    BucketTile,
    BucketGroupTile,
  },
  mounted() {
    this.$store.dispatch("setPageTitle", `${this.pluralizedName} Overview`)
    this.$store.dispatch("setCaseTypeBuckets", this.caseTypeId)
  },
  data: () => ({
    createNewDialog: false,
    loading: false,
    bucketsToggled: false,
    orderByOptions: [
      { text: "Name", value: "name" },
      { text: "Date Created", value: "createdAt" },
    ],
    orderSelection: "name",
    searchTerm: "",
    localBucketList: undefined,
    localBucketGroupList: undefined,
  }),
  computed: {
    ...mapState({
      caseTypes: (state) => state.caseTypes.all,
      cbroute: (state) => state.cbroute,
      isAdminMode: (state) => state.currentUser.isAccountAdmin,
      permissions: (state) => state.ui.grantedPermissions,
    }),
    caseTypeId() {
      return parseInt(this.cbroute.params.case_type_id)
    },
    caseType() {
      if (this.caseTypes) {
        return this.caseTypes.find((ct) => ct.id === this.caseTypeId)
      } else {
        return []
      }
    },
    pluralizedName() {
      return Helpers.pluralize(Helpers.caseTypeName(this.caseType))
    },
    orderedBuckets: {
      get() {
        if (!this.localBucketList) {
          this.bucketSearch()
        }
        return this.localBucketList
      },
      set(newlist) {
        this.localBucketList = newlist
      },
    },
    orderedBucketGroups: {
      get() {
        if (!this.localBucketGroupList) {
          this.bucketGroupSearch()
        }
        return this.localBucketGroupList
      },
      set(newlist) {
        this.localBucketGroupList = newlist
      },
    },
    hasImportPermission() {
      if (this.isAdminMode) {
        return true
      }
      const perm = "Import-" + this.caseType.code
      return this.permissions.includes(perm)
    },
  },
  methods: {
    importRoute: (caseTypeId) => `/case-types/${caseTypeId}/import`,
    bucketGroupCreateRoute: (caseTypeId) => `/case-types/${caseTypeId}/bucket-groups/new`,
    bucketCreateRoute: (caseTypeId) => `/case-types/${caseTypeId}/buckets/new`,
    bucketSearch() {
      if (!this.caseType || !Array.isArray(this.caseType.buckets)) {
        this.orderedBuckets = []
      } else {
        let newList = this.caseType.buckets.slice()

        if (this.searchTerm) {
          const term = (this.searchTerm || "").toLowerCase()
          newList = newList.filter((bucket) => (bucket && bucket.name ? bucket.name.toLowerCase() : "").includes(term))
        }
        newList = this.handleOrderByField(newList)

        this.orderedBuckets = newList
      }
    },
    bucketGroupSearch() {
      if (!this.caseType || !Array.isArray(this.caseType.bucketGroups)) {
        this.orderedBucketGroups = []
      } else {
        let newList = this.caseType.bucketGroups.slice()

        if (this.searchTerm) {
          const term = (this.searchTerm || "").toLowerCase()
          newList = newList.filter((bucketGroup) => (bucketGroup && bucketGroup.name ? bucketGroup.name.toLowerCase() : "").includes(term))
        }
        newList = this.handleOrderByField(newList)

        this.orderedBucketGroups = newList
      }
    },
    handleOrderByField(newList) {
      if (this.orderSelection) {
        switch (this.orderSelection) {
          case "name":
            newList = this.orderArrayByField(newList, "name", true)
            break
          case "createdAt":
            newList = this.orderArrayByField(newList, "id", false)
            break
          default:
            newList = this.orderArrayByField(newList, "name", true)
        }
      }
      return newList
    },
    orderArrayByField(arr, property, isString) {
      if (arr && arr.length > 0) {
        return arr.sort(function (a, b) {
          const aVal = a && a[property]
          const bVal = b && b[property]
          if (isString) {
            const aStr = (aVal != null ? String(aVal) : "").toLowerCase()
            const bStr = (bVal != null ? String(bVal) : "").toLowerCase()
            if (aStr < bStr) return -1
            if (aStr > bStr) return 1
          } else {
            if (aVal < bVal) return -1
            if (aVal > bVal) return 1
          }
          return 0
        })
      }
      return []
    },
    async reload() {
      await this.$store.dispatch("setCurrentCaseTemplate", { caseTypeId: this.caseTypeId })
      this.bucketGroupSearch()
      this.bucketSearch()
    },
  },
  watch: {
    caseTypeId() {
      this.reload()
    },
    "caseType.buckets": {
      handler() {
        this.localBucketList = undefined
        this.bucketSearch()
      },
      deep: false,
    },
    pluralizedName() {
      this.$store.dispatch("setPageTitle", `${this.pluralizedName} Overview`)
    },
    searchTerm() {
      this.bucketSearch()
      this.bucketGroupSearch()
    },
    orderSelection() {
      this.bucketSearch()
      this.bucketGroupSearch()
    },
  },
}
</script>

<style>
.fixed-header .action .v-btn {
  margin: initial;
}
.action {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
